import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

type Contact = { name: string; email: string; phone: string; coordinator?: boolean };
const blank = (): Contact => ({ name: "", email: "", phone: "" });

export function CharterEditDialog({ r, onClose, onSaved }: { r: any; onClose: () => void; onSaved: (row: any) => void }) {
  const [rows, setRows] = useState<Contact[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRows(r.contacts?.length ? r.contacts.map((c: Contact) => ({ ...c })) : [blank()]); }, [r]);
  const set = (i: number, k: keyof Contact, v: string) => setRows(rows.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const pick = (i: number) => setRows(rows.map((c, j) => (j === i ? { ...c, coordinator: !c.coordinator } : c)));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/dow/charters/${r.charter}`, { contacts: rows });
      toast.success(`Contacts saved for ${r.name}`);
      onSaved(data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="dow-charter-edit-dialog">
        <DialogHeader><DialogTitle>Edit contacts: {r.name} (Charter {r.charter})</DialogTitle></DialogHeader>
        <p className="text-sm text-[#64748B]">Add everyone who should receive this charter's lists. Each email can be chosen when you click Send lists. Mark each <b>Compliance Coordinator</b> (you can mark several); certificates go to all of them in one email. If nobody is marked, every contact with an email receives them.</p>
        <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
          {rows.map((c, i) => (
            <div key={i} className={`grid gap-2 rounded-lg border p-2 sm:grid-cols-[1fr_1.3fr_0.9fr_auto_auto] ${c.coordinator ? "border-[#14B8A6] bg-[#F0FDFA]" : "border-[#E2E8F0]"}`} data-testid={`dow-charter-contact-${i}`}>
              <input className="field-input" placeholder="Name" value={c.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`dow-charter-contact-name-${i}`} />
              <input className="field-input" type="email" placeholder="Email" value={c.email} onChange={(e) => set(i, "email", e.target.value)} data-testid={`dow-charter-contact-email-${i}`} />
              <input className="field-input" placeholder="Phone" value={c.phone} onChange={(e) => set(i, "phone", e.target.value)} data-testid={`dow-charter-contact-phone-${i}`} />
              <button type="button" aria-pressed={!!c.coordinator} title="Certificates are addressed to the Compliance Coordinator" onClick={() => pick(i)} disabled={!c.email.trim()}
                className={`btn-sm justify-center whitespace-nowrap disabled:opacity-40 sm:w-40 ${c.coordinator ? "btn-primary" : "btn-secondary"}`} data-testid={`dow-charter-contact-coordinator-${i}`}>
                <BadgeCheck className="h-4 w-4" /> {c.coordinator ? "Coordinator" : "Make coordinator"}
              </button>
              <button className="btn-secondary btn-sm text-[#B91C1C]" title="Remove contact" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`dow-charter-contact-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <button className="btn-secondary btn-sm w-fit" disabled={rows.length >= 25} onClick={() => setRows([...rows, blank()])} data-testid="dow-charter-contact-add"><Plus className="h-4 w-4" /> Add contact</button>
        <div className="flex justify-end gap-2">
          <button className="btn-secondary btn-sm" onClick={onClose} data-testid="dow-charter-edit-cancel">Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy} onClick={save} data-testid="dow-charter-edit-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save contacts</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
